import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import { extendSchemaEnum } from "./extract-document-content.step";

/**
 * Extract Document Content Step v3
 *
 * Extends v2 with typed-projection output sockets compiled from a pinned
 * SchemaDefinitionVersion. Two output shapes are supported via
 * `projectionOutputMode`:
 *  - `per_field` (default): one dynamic socket per declared field
 *  - `single_object`: a single `array<json>` projection socket
 *
 * The base v2 outputs are still emitted as static sockets. The projection
 * sockets are computed on the editor side from the loaded snapshot and
 * persisted on save (mirror pattern used by `split_document_v2` and
 * `identify_customer`).
 */

export const extractDocumentContentV3Step = defineStepConfig({
  type: "extract_document_content_v3",
  name: "Extract Document Content (V3)",
  description:
    "Run OCR + extraction and emit typed projection sockets from a pinned schema version",
  category: "AI & Agents",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email whose attachments should be processed",
    ),
    extractionConfig: z
      .object({
        type: z.literal("EXTRACT"),
        schema: z.record(z.string(), z.unknown()),
        advancedOptions: z.record(z.string(), z.unknown()).optional(),
      })
      .describe(
        "Optional extraction schema; defaults to Extend AI schema for the configured type",
      )
      .optional(),
  }),

  // `.catchall(z.unknown())` so the dynamic projection keys (`field:<name>` or
  // `extractedFields`) the handler spreads in survive Zod's parse step — by
  // default `z.object({...})` strips unknown keys, which would silently drop
  // every typed projection socket payload.
  outputSchema: z
    .object({
      inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
        "Pass-through of the inbound email for downstream steps",
      ),
      documents: z
        .array(entityOfType(SystemEntity.CORE_DOCUMENT))
        .describe(
          "Array of document entity references (for fanout to per-document processing)",
        ),
      documentOcrResults: z.array(
        z.object({
          document: entityOfType(SystemEntity.CORE_DOCUMENT),
          ocrResult: entityOfType(
            SystemEntity.CORE_DOCUMENT_OCR_RESULT,
          ).optional(),
          status: z.enum(["completed", "failed", "skipped"]),
          extractedTextLength: z.number().optional(),
          error: z.string().optional(),
        }),
      ),
      processedCount: z.number(),
      failedCount: z.number(),
      skippedCount: z.number(),
    })
    .catchall(z.unknown()),

  configSchema: z.object({
    extendSchemaType: extendSchemaEnum.optional(),
    extractionSchemaDefinitionId: z
      .uuid()
      .optional()
      .describe(
        "Stored extraction schema definition to compile and use for all processed documents",
      ),
    extractionSchemaDefinitionVersionId: z
      .uuid()
      .optional()
      .describe(
        "Pinned version of the schema definition. The version's immutable snapshot drives both extraction and typed projection sockets.",
      ),
    projectionOutputMode: z
      .enum(["per_field", "single_object"])
      .optional()
      .describe(
        'Output shape: "per_field" emits one dynamic socket per declared field (default); "single_object" emits one array<json> projection socket carrying the whole payload.',
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "15 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
    nonRetryableErrorTypes: [
      "InvalidFileFormatError",
      "UnsupportedMimeTypeError",
    ],
  },

  // Surfaces the Evals tab in the step editor. The shared
  // ExtractDocumentContentV2Evaluator reshapes step output during seeding
  // and field-diffs the extractions JSONB.
  evalConfig: {
    evalOutputKeys: ["extractions"],
    outputFormat: "object",
  },
});
